/**
 * Workspace Audio — synthèse vocale Chatterbox (25 langues + clonage
 * zero-shot) et musique/effets sonores Stable Audio Open.
 */
import { APIClient } from "../../api/client.js";
import { accordion } from "../../components/Accordion.js";
import { executionPicker } from "../../components/ExecutionPicker.js";
import { generateButton } from "../../components/GenerateButton.js";
import { createReadinessPanel } from "../../components/ReadinessPanel.js";
import { toast } from "../../components/Toast.js";
import { h } from "../../components/dom.js";
import { runGeneration, serverGenerates } from "../../services/execution.js";
import type { WorkspaceDef } from "../../shell/types.js";
import { store } from "../../state/store.js";
import type { AudioGenerationRequest } from "../../types/index.js";

const LANGUAGES: { value: string; label: string }[] = [
	{ value: "en", label: "Anglais" },
	{ value: "fr", label: "Français" },
	{ value: "es", label: "Espagnol" },
	{ value: "de", label: "Allemand" },
	{ value: "it", label: "Italien" },
	{ value: "pt", label: "Portugais" },
	{ value: "nl", label: "Néerlandais" },
	{ value: "pl", label: "Polonais" },
	{ value: "ru", label: "Russe" },
	{ value: "tr", label: "Turc" },
	{ value: "ar", label: "Arabe" },
	{ value: "he", label: "Hébreu" },
	{ value: "hi", label: "Hindi" },
	{ value: "ja", label: "Japonais" },
	{ value: "ko", label: "Coréen" },
	{ value: "zh", label: "Chinois" },
	{ value: "sv", label: "Suédois" },
	{ value: "no", label: "Norvégien" },
	{ value: "da", label: "Danois" },
	{ value: "fi", label: "Finnois" },
	{ value: "el", label: "Grec" },
	{ value: "ms", label: "Malais" },
	{ value: "sw", label: "Swahili" },
];

export const audioWorkspace: WorkspaceDef = {
	id: "audio",
	title: "Audio",
	icon: "audio",
	build(container) {
		container.appendChild(h("h2", { class: "workspace-title" }, "Génération audio"));

		let backend: "chatterbox" | "stable_audio" = "chatterbox";
		let referenceRef: string | null = null;

		const text = h("textarea", {
			rows: 4,
			maxLength: 2000,
			placeholder: "Le texte à dire, ou le son à créer…",
		}) as HTMLTextAreaElement;
		const language = h(
			"select",
			null,
			...LANGUAGES.map((l) => h("option", { value: l.value }, l.label))
		) as HTMLSelectElement;
		const referenceFile = h("input", {
			type: "file",
			accept: "audio/wav,audio/mpeg,audio/flac,audio/ogg",
		}) as HTMLInputElement;
		const referenceStatus = h("small", { class: "help-text", "aria-live": "polite" });
		const duration = h("input", {
			type: "number",
			value: 10,
			min: 1,
			max: 47,
			step: 1,
		}) as HTMLInputElement;
		const seed = h("input", {
			type: "number",
			value: -1,
			step: 1,
			title: "-1 = aléatoire",
		}) as HTMLInputElement;

		// What this family still needs on the paired machine (a download, a
		// licence), with its button -- the Library's own answer.
		const readiness = createReadinessPanel();

		const backendSelect = h(
			"select",
			{
				onchange: () => {
					backend = backendSelect.value as "chatterbox" | "stable_audio";
					applyBackend();
				},
			},
			h("option", { value: "chatterbox" }, "Une voix qui lit votre texte"),
			h("option", { value: "stable_audio" }, "Musique ou bruitage")
		) as HTMLSelectElement;

		const voiceGroup = h(
			"div",
			{ class: "form-group" },
			h("label", { class: "field-label" }, "Langue"),
			language,
			h("label", { class: "field-label" }, "Imiter une voix (facultatif)"),
			h("div", { class: "file-input-row" }, referenceFile, referenceStatus),
			h(
				"p",
				{ class: "help-text" },
				"N'utilisez que des voix que vous avez le droit d'utiliser. Extrait de 30 s au plus."
			)
		);
		const stableGroup = h(
			"div",
			{ class: "form-group" },
			h("label", { class: "field-label" }, "Durée (secondes)"),
			duration,
			h("p", { class: "help-text" }, "Décrivez un son ou une ambiance plutôt que des paroles.")
		);
		const runButton = generateButton("Générer l'audio", () => void generate());

		referenceFile.addEventListener("change", () => {
			const file = referenceFile.files?.[0];
			if (file) void uploadReference(file);
		});

		container.append(
			h(
				"div",
				{ class: "form-group" },
				h("label", { class: "field-label" }, "Créer"),
				backendSelect
			),
			h("div", { class: "form-group" }, h("label", { class: "field-label" }, "Texte"), text),
			voiceGroup,
			stableGroup,
			accordion({
				title: "Réglages avancés",
				children: [
					h(
						"div",
						{ class: "form-group" },
						h("label", { class: "field-label" }, "Graine (-1 = au hasard)"),
						seed
					),
				],
			}).element,
			readiness.element,
			executionPicker(),
			runButton.element
		);

		function applyBackend(): void {
			voiceGroup.classList.toggle("hidden", backend !== "chatterbox");
			stableGroup.classList.toggle("hidden", backend !== "stable_audio");
			readiness.show(backend);
		}
		applyBackend();

		async function uploadReference(file: File): Promise<void> {
			referenceStatus.textContent = "Envoi…";
			try {
				const response = await APIClient.uploadAudioReference(file);
				referenceRef = response.source_ref;
				referenceStatus.textContent = `${file.name} (${response.duration_seconds.toFixed(1)} s)`;
			} catch (error) {
				referenceRef = null;
				referenceStatus.textContent = "";
				toast(
					error instanceof Error ? error.message : "Échec de l'envoi de la référence.",
					"error"
				);
			}
		}

		async function generate(): Promise<void> {
			if (!text.value.trim()) {
				toast("Entrez d'abord le texte à synthétiser.", "warning");
				return;
			}
			const payload: Record<string, unknown> = {
				text: text.value,
				backend,
				language: backend === "chatterbox" ? language.value : undefined,
				reference_audio: backend === "chatterbox" ? referenceRef : null,
				duration_seconds: backend === "stable_audio" ? Number(duration.value) || 10 : undefined,
				seed: Number(seed.value) === -1 ? null : Number(seed.value),
			};
			try {
				await runGeneration({
					kind: "audio",
					payload,
					hostedFailureMessage: "La génération audio sur votre ordinateur a échoué.",
					runLocal: async () => {
						const response = await APIClient.generateAudio(
							payload as unknown as AudioGenerationRequest
						);
						if (!response.success || !response.audio_url) {
							toast(response.error ?? "Échec de la génération audio.", "error");
							return;
						}
						store.setState({
							currentResult: {
								kind: "audio",
								url: response.audio_url,
								filename: response.filename ?? "audio.wav",
								generationTime: response.generation_time ?? undefined,
							},
							historyVersion: store.get().historyVersion + 1,
						});
					},
				});
			} catch (error) {
				toast(error instanceof Error ? error.message : "Échec de la génération audio.", "error");
			}
		}
	},
};
